/**
 * Diagnostics report - "Send to my computer" (2026-10-05).
 *
 * WHY. The operator gets gig freezes they cannot date ("dont know when but i
 * had to restart the app"). The iPad's black box holds every drop and every
 * restart with times, but reading it meant Copy on the iPad and a paste on a
 * Windows PC - a step that does not happen after a gig. One tap now writes
 * the whole record to every connected OPBridge's doc store, where it lands on
 * that machine's disk (`...\OPButtons\docs\__diag_report_<device>_<stamp>.json`)
 * and can be read there without the iPad.
 *
 * THE ENVELOPE IS THE SUITE'S, NOT OURS. This is step 1 of a suite-wide
 * "send a report to OverPoweredApps" (`.notepads/diagnostics-reports.md`): the
 * same envelope will later go to an OPApps inbox as well. `version: 0` marks it
 * as OPButtons' draft until OPApps agrees v1, so nothing reading it later
 * mistakes the draft for the agreed shape.
 *
 * NEVER A SECRET. A bridge row carries its bearer and URL; a report carries
 * neither - only the name the operator knows the machine by, its node id and
 * its state. `diag_report_sends_to_every_bridge` asserts the seeded bearer is
 * absent from the sent body, with a positive control.
 *
 * Read-only on the app. The only writes are the report docs themselves, and
 * only when the operator taps Send. Project sync reads `project-*` docs only,
 * so a report can never be mistaken for a project (same as `__frame_report_*`).
 */

import { bridges, getConnection } from '../opbridge/store.ts';
import { deviceTag } from '../layout/frameReport.ts';
import { blackBoxVerdict, blackBoxHistorySummary, readBlackBoxEvents, exportBlackBox, type BlackBoxEvent } from './blackBox.ts';
import { readCapturedErrors, type CapturedError } from './errors.ts';

export interface DiagReportBridge {
  /** The machine's own name when it has reported one, else the row's name. */
  name: string;
  node: string | null;
  status: string;
  build: string | null;
  platform: string | null;
}

export interface DiagReport {
  schema: 'op.diag-report';
  /** 0 = OPButtons' draft, until OPApps agrees v1. */
  version: 0;
  id: string;
  app: 'OPButtons';
  build: string;
  at: string;
  /** The suite envelope's types are freeze / bug / idea / question; this button
   *  asks nothing, so it says so rather than guessing. */
  type: 'unspecified';
  note: string;
  device: { kind: string; userAgent: string; standalone: boolean; screen: { w: number; h: number; dpr: number } };
  bridges: DiagReportBridge[];
  blackBox: { verdict: string; history: string; events: BlackBoxEvent[]; text: string };
  errors: CapturedError[];
}

function isStandalone(): boolean {
  try {
    return (navigator as unknown as { standalone?: boolean }).standalone === true
      || window.matchMedia('(display-mode: standalone)').matches;
  } catch {
    return false;
  }
}

/** `20261005T171503Z` - sortable, and safe in a doc id and a file name. */
function stamp(d: Date): string {
  return d.toISOString().replace(/\.\d{3}Z$/, 'Z').replace(/[-:]/g, '');
}

/* TOKEN SHAPES, removed from the WHOLE report before it leaves the device
 * (2026-10-05, OPApps' review of v0). The bridge rows are left out by
 * construction, but error messages, stacks and the black box's labels are
 * free text: a failed request can put an OP ID token or a pairing key into an
 * error message, and from there into a report. op.report v1 lists the same
 * shapes the server scans for; the server's scan is the backstop, this is the
 * guard. Each pattern stops at a quote or a backslash so a replacement can
 * never break the JSON it runs over. */
const SECRET_SHAPES: RegExp[] = [
  /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}/g, // a JWT (OP ID / Supabase tokens)
  /\bop(?:dev|req|svc)_[A-Za-z0-9_-]{6,}/g, // OP ID device / claim / service keys (opdev_ = pairing)
  /\bopb_[A-Za-z0-9_-]{6,}/g, // OPBridge bearers
  /\bsb_(?:secret|publishable)_[A-Za-z0-9_-]{6,}/g, // Supabase keys
  /\b(?:Bearer|OAuth)\s+[^\s"\\]+/gi, // an Authorization header's value
];

/** Replace every token shape with `[removed]`. Exported for the leak smoke. */
export function redactSecrets(text: string): string {
  let out = text;
  for (const re of SECRET_SHAPES) out = out.replace(re, '[removed]');
  return out;
}

export function buildDiagReport(note = ''): DiagReport {
  const raw = buildRawReport(note);
  return JSON.parse(redactSecrets(JSON.stringify(raw))) as DiagReport;
}

function buildRawReport(note: string): DiagReport {
  const now = new Date();
  const device = deviceTag();
  return {
    schema: 'op.diag-report',
    version: 0,
    id: `${device}_${stamp(now)}`,
    app: 'OPButtons',
    build: __BUILD_SHA__,
    at: now.toISOString(),
    type: 'unspecified',
    note,
    device: {
      kind: device,
      userAgent: navigator.userAgent,
      standalone: isStandalone(),
      screen: { w: screen.width, h: screen.height, dpr: window.devicePixelRatio || 1 },
    },
    bridges: bridges().map((cfg) => {
      const conn = getConnection(cfg.id);
      const h = conn?.health() ?? null;
      return {
        name: h?.machine_name || cfg.name,
        node: cfg.nodeId,
        status: conn ? conn.status() : 'none',
        build: h?.build_version ?? null,
        platform: h?.platform ?? null,
      };
    }),
    blackBox: {
      verdict: blackBoxVerdict(),
      history: blackBoxHistorySummary(),
      events: readBlackBoxEvents(),
      text: exportBlackBox(),
    },
    errors: readCapturedErrors(),
  };
}

/** The report as text, for the Copy button: the black box export plus the
 *  captured errors, so Copy and Send never tell different stories. */
export function diagReportText(report: DiagReport): string {
  const errs = report.errors.length === 0
    ? '(none recorded)'
    : report.errors.map((e) => [
      new Date(e.first).toISOString(), new Date(e.last).toISOString(), `x${e.count}`, e.build, e.where, e.message,
    ].join(',')).join('\n');
  return `${report.blackBox.text}\n\n--- uncaught errors (first,last,count,build,where,message) ---\n${errs}\n`;
}

export interface DiagSendResult {
  /** Machines that stored it, by the name the operator knows them by. */
  sentTo: string[];
  failed: string[];
  /** Bridges that were connected and therefore tried. */
  tried: number;
}

/** Write the report to every connected OPBridge. Never throws. */
export async function sendDiagReport(report: DiagReport): Promise<DiagSendResult> {
  const docId = `__diag_report_${report.id}`;
  const result: DiagSendResult = { sentTo: [], failed: [], tried: 0 };
  for (const cfg of bridges()) {
    const conn = getConnection(cfg.id);
    if (!conn || conn.status() !== 'connected') continue;
    result.tried++;
    const name = conn.health()?.machine_name || cfg.name;
    try {
      // A fresh doc per report (the id carries the second), so base_rev 0.
      await conn.httpClient().docPut('opbuttons', docId, { base_rev: 0, body: report });
      result.sentTo.push(name);
    } catch {
      result.failed.push(name);
    }
  }
  return result;
}
